import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { trainingAPI } from "@api/TrainingAPI";
import { setTeamPresets } from "@store/actions/team.actions";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { PresetSelection, PresetType } from "../PresetSelection";
import { CompactStatTile as CompactStatTileJsx } from "../StatTileV2/CompactStatTile.jsx";

// TypeScript reads the JSX default `onChange = () => {}` as a function with no argument.
const CompactStatTile = CompactStatTileJsx as React.FC<any>;
const PRESET_TYPE = PresetType.Helmet;
const DEFAULT_PRESET = "Default";
// Load, High Impacts, Alert Impacts and Top Impacts. A team without a saved helmet default gets these.
const FALLBACK_DEFAULTS = ["helmet-1-avg", "helmet-12-avg", "helmet-13-avg", "helmet-6-avg"];

interface Props {
  /** The Player page sets it: the tiles show that player. */
  playerId?: number;
  startDate: string | null;
  endDate: string | null;
  comparisonOption: string;
  setComparisonOption: (value: string) => void;
}

// The preset row and the tiles of the GPS tab (TeamAvgTable), with the helmet metrics.
// HelmetImpact chooses the filter (player or team) and passes its dates and comparison here.
export function HelmetTiles({ playerId, startDate, endDate, comparisonOption, setComparisonOption }: Props) {
  const dispatch = useDispatch();
  const { user } = useAuth0();
  const {
    misc: { comparison_types },
    helmet_metrics: helmetMetrics,
    presets,
  } = useSelector(({ team }: any) => team);

  // null until /teamkeystats answers. The tiles wait for it, so they do not load the fallback first.
  const [statDefaults, setStatDefaults] = useState<string[] | null>(null);
  const [statMetrics, setStatMetrics] = useState<string[]>([]);
  const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    trainingAPI
      .getDefaultStats()
      .then((stats: any) => setStatDefaults(stats?.helmet?.length ? stats.helmet : FALLBACK_DEFAULTS))
      .catch((e: unknown) => {
        console.error(e);
        setStatDefaults(FALLBACK_DEFAULTS);
      });
  }, []);

  const dropdowns = useMemo(
    () => (helmetMetrics ?? []).map((m: any) => ({ key: m.unique_identifier, label: m.name })),
    [helmetMetrics],
  );
  const helmetPresets = useMemo(() => (presets ?? []).filter((p: any) => p.type === PRESET_TYPE), [presets]);

  const handleChangeMetric = (index: number) => (key: string) => {
    setStatMetrics((current) => current.map((m, i) => (i === index ? key : m)));
    setUnsavedChanges(true);
  };

  const handleSave = async () => {
    try {
      if (currentSelectedTilePreset === DEFAULT_PRESET) {
        await trainingAPI.updateDefaultStats(statMetrics, "helmet");
        setStatDefaults(statMetrics);
      } else {
        const preset = helmetPresets.find((p: any) => p.name === currentSelectedTilePreset);
        if (!preset) throw new Error(`No helmet preset named ${currentSelectedTilePreset}`);
        await trainingAPI.updatePreset({
          name: currentSelectedTilePreset, type: PRESET_TYPE, config: statMetrics, preset_id: preset.preset_id,
        });
        dispatch(setTeamPresets(presets.map((p: any) => (p === preset ? { ...p, config: statMetrics } : p))));
      }
      message.success("Saved!");
    } catch (e) {
      console.error(e);
      message.error("Error. Unable to edit ");
    }
  };

  if (!statDefaults) return null;

  return (
    <div className="flex flex-col justify-center items-center w-full">
      <Row gutter={16} style={{ width: "84%", marginBottom: 16, justifyContent: "center", gap: "10px" }}>
        <PresetSelection
          presets={helmetPresets}
          user={user!}
          presetType={PRESET_TYPE}
          statDefaults={statDefaults}
          currStatMetrics={statMetrics}
          setStatMetrics={setStatMetrics}
          currentSelectedTilePreset={currentSelectedTilePreset}
          setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
          comparisonTypes={comparison_types}
          comparisonOption={comparisonOption}
          setComparisonOption={setComparisonOption}
          handleSave={handleSave}
          warnUnsavedChanges={unsavedChanges}
          setWarnUnsavedChages={setUnsavedChanges}
        />
      </Row>
      <Row gutter={16} className="helmet-tiles">
        {statMetrics.map((metric, i) => (
          <Col span={6} key={i}>
            <CompactStatTile
              dropdowns={dropdowns}
              metric_identifier_with_stat={metric}
              onChange={handleChangeMetric(i)}
              option={comparisonOption}
              startDate={startDate}
              endDate={endDate}
              type={playerId ? "player" : "team"}
              playerId={playerId}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
}
